/** @file Decodes the overlay font page (base64 PNG in FontAtlas) into a premultiplied image, memoized. */
import { decodePng } from '@pose2d/image-io/decodePng';
import { premultiply } from '@pose2d/image-io/premultiply';
import type { RgbaImage } from '@pose2d/math/types';
import type { FontAtlas } from '@pose2d/overlay/types';
import { invariant } from '@pose2d/std/invariant';

const ALPHABET = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789+/';
let memo: { key: string; page: RgbaImage } | null = null;

/** Standard base64 to bytes (padding and whitespace ignored); packages have no atob (lib ES2023, no DOM). */
function fromBase64(text: string): Uint8Array {
  const clean = text.replace(/[\s=]/g, '');
  const out = new Uint8Array(Math.floor((clean.length * 3) / 4));
  let acc = 0;
  let bits = 0;
  let n = 0;
  for (const ch of clean) {
    const v = ALPHABET.indexOf(ch);
    invariant(v >= 0, `fontPage: "${ch}" is not base64`);
    acc = ((acc << 6) | v) & 0xffffff;
    bits += 6;
    if (bits >= 8) {
      bits -= 8;
      out[n++] = (acc >> bits) & 255;
    }
  }
  return out.subarray(0, n);
}

/** The only font-atlas decoder (W2-23): last TexturePages page (raster samples it nearest), LabelFont, editor uploadPages. */
export function fontPage(font: FontAtlas): RgbaImage {
  if (memo?.key !== font.pngBase64) {
    const page = premultiply(decodePng(fromBase64(font.pngBase64)));
    invariant(page.width === font.pageW && page.height === font.pageH, `fontPage: PNG is ${page.width}x${page.height}, atlas says ${font.pageW}x${font.pageH}`);
    memo = { key: font.pngBase64, page };
  }
  return memo.page;
}
